"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import type { SearchableField } from "@/lib/search/schema";
import type { FilterSelection, SearchSort } from "@/lib/search/parser";
import type { EssenceSearchHit } from "@/lib/search/documents";

export interface SearchResponse {
  found: number;
  page: number;
  hits: EssenceSearchHit[];
  facet_counts?: Record<string, { value: string; count: number }[]>;
}

export interface UseSearchOptions {
  query: string;
  fields: SearchableField[];
  filters: FilterSelection[];
  sort: SearchSort;
  perPage?: number;
  /** Restrict to these Essence ids (Favorites view). */
  ids?: string[];
  enabled?: boolean;
  /** Server-rendered first page, used to seed the cache for the default state. */
  initialPage?: SearchResponse;
}

export function useSearch({
  query,
  fields,
  filters,
  sort,
  perPage = 24,
  ids,
  enabled = true,
  initialPage,
}: UseSearchOptions) {
  return useInfiniteQuery<SearchResponse, Error>({
    queryKey: ["search", query, fields, filters, sort, perPage, ids ?? null],
    queryFn: async ({ pageParam }) => {
      const response = await fetch("/api/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          fields,
          filters,
          sort,
          page: pageParam as number,
          perPage,
          ...(ids ? { ids } : {}),
        }),
      });

      if (!response.ok) {
        throw new Error("Search request failed");
      }

      return response.json() as Promise<SearchResponse>;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.found / perPage);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    staleTime: 2 * 60 * 1000,
    placeholderData: (previous) => previous,
    enabled,
    initialData: initialPage ? { pages: [initialPage], pageParams: [1] } : undefined,
  });
}
